import { AlertCircle, AlertTriangle, Info } from "lucide-react";

export const API = process.env.REACT_APP_BACKEND_URL;

export const SEVERITY_META = {
  red: {
    label: "Red line",
    bg: "bg-rose-50",
    border: "border-rose-200",
    text: "text-rose-600",
    dot: "bg-rose-500",
    icon: AlertCircle,
  },
  amber: {
    label: "Amber",
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-600",
    dot: "bg-amber-500",
    icon: AlertTriangle,
  },
  green: {
    label: "Note",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-600",
    dot: "bg-emerald-500",
    icon: Info,
  },
};

export const STATUS_LABEL = {
  uploading: "Checking evidence pack…",
  preflight: "Evidence pack ready",
  queued: "Queued",
  reading_emails: "Reading email chains…",
  analysing: "Analysing contract…",
  verifying: "Verifying and building the coverage checklist…",
  done: "Ready for review",
  error: "Failed",
};

export const IN_FLIGHT = new Set(["queued", "reading_emails", "analysing", "verifying"]);

export const COVERAGE_STATUS = {
  carried: { label: "Carried", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  conflicts: { label: "Conflicts", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  missing: { label: "Missing", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  not_applicable: { label: "Not applicable", cls: "bg-stone-100 text-stone-500 border-stone-200" },
  not_checked: { label: "Not checked", cls: "bg-stone-50 text-stone-400 border-stone-200" },
};

export const LEDGER_STATUS = {
  reflected: { label: "Reflected", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  missing: { label: "Missing", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  contradicted: { label: "Contradicted", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  unclear: { label: "Unclear", cls: "bg-stone-100 text-stone-500 border-stone-200" },
};

export const PREVIOUS_STATUS = {
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partially_resolved: { label: "Partially resolved", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  unresolved: { label: "Unresolved", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  not_applicable: { label: "No longer applies", cls: "bg-stone-100 text-stone-500 border-stone-200" },
  not_checked: { label: "Not checked", cls: "bg-stone-50 text-stone-400 border-stone-200" },
};

export const EMAIL_EXTENSIONS = [".msg", ".eml", ".pdf", ".txt"];

export function fmtGbp(v) {
  if (v == null || Number.isNaN(Number(v))) return "n/a";
  return `£${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export async function downloadBlob(url, fallbackName, { method = "GET", data } = {}) {
  const axios = (await import("axios")).default;
  const res = await axios({ url, method, data, responseType: "blob", timeout: 90000 });
  const disp = res.headers["content-disposition"] || "";
  const m = disp.match(/filename="?([^"]+)"?/i);
  const filename = m ? m[1] : fallbackName;
  const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
  return res;
}

export async function blobErrorDetail(err, fallback = "Request failed.") {
  let detail = err?.message || fallback;
  try {
    const blob = err?.response?.data;
    if (blob && typeof blob.text === "function") {
      const txt = await blob.text();
      try {
        detail = JSON.parse(txt).detail || txt || detail;
      } catch {
        detail = txt || detail;
      }
    } else if (err?.response?.data?.detail) {
      detail = err.response.data.detail;
    }
  } catch {
    /* keep fallback */
  }
  return typeof detail === "string" ? detail : JSON.stringify(detail);
}

export const Pill = ({ meta, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center text-[10px] font-mono uppercase tracking-[0.12em] border rounded px-1.5 py-0.5 whitespace-nowrap ${meta.cls}`}
  >
    {meta.label}
  </span>
);

export const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-2">{children}</p>
);
